/**
 * 배포 중간에 열려 있던 탭을 구해낸다.
 *
 * 이 앱은 ALB → ECS 직결이고 CDN 이 없다. `_next/static/chunks/*` 를 컨테이너가 직접 서빙하므로,
 * 롤링 교체가 끝나고 구 태스크가 drain 되면(PRESTOP_DELAY_MS=60000) 구 빌드의 청크 파일은
 * 어디에도 남지 않는다. 파일명에 콘텐츠 해시가 박혀 있어 빌드가 바뀌면 이름 자체가 달라진다.
 *
 * 그래서 배포 전부터 열려 있던 탭이 그 뒤에 청크를 지연 로드하면 404 가 난다. 이 앱에서
 * 지연 로드가 걸린 곳은 블로그 에디터(tiptap)와 분석 그래프(xyflow) — 둘 다 오래 열어두고
 * 작업하는 화면이라 하필 제일 잘 걸린다.
 *
 * 이 실패는 리렌더로 풀리지 않는다. 없어진 파일은 다시 렌더해도 없다. 서버에서 문서를 새로
 * 받아와야 새 빌드의 청크 이름을 알게 된다 — 그래서 reset() 이 아니라 하드 리로드가 필요하다.
 */

/**
 * 청크 로드 실패인가.
 *
 * 번들러·브라우저마다 표현이 다르다. webpack 은 name 을 'ChunkLoadError' 로 세우고,
 * 네이티브 동적 import 실패는 브라우저마다 문구가 갈린다(Chrome/Firefox/Safari 각각 다르다).
 * 그래서 이름 하나만 보지 않고 문구도 함께 본다.
 *
 * 프로덕션에서 판별이 되는지가 관건이다 — React 는 서버 컴포넌트 예외의 메시지를 지우고
 * digest 만 넘기지만, 청크 로드 실패는 클라이언트에서 나는 예외라 name·message 가 살아 있다.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const name = (error as { name?: unknown }).name;
  if (name === 'ChunkLoadError') return true;

  const message = (error as { message?: unknown }).message;
  if (typeof message !== 'string') return false;

  return (
    /loading chunk \S+ failed/i.test(message) || // webpack
    /loading css chunk/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) || // Chrome
    /error loading dynamically imported module/i.test(message) || // Firefox
    /importing a module script failed/i.test(message) // Safari
  );
}

/** 자동 새로고침을 이미 한 번 썼는지 기억하는 키. 경로별로 따로 센다. */
const reloadFlagKey = (pathname: string): string => `lk:chunk-reload:${pathname}`;

/**
 * 청크 로드 실패면 딱 한 번 하드 리로드한다. 리로드했으면 true.
 *
 * 한 번으로 제한하는 게 핵심이다. 리로드 후에도 같은 에러가 난다면 그건 배포 스큐가 아니라
 * 다른 고장이라는 뜻인데, 가드가 없으면 무한 새로고침에 빠져 사용자가 화면을 볼 수조차 없다.
 * 두 번째부터는 false 를 돌려주고 호출부가 평소의 에러 화면을 보여준다.
 *
 * sessionStorage 를 쓴다 — 탭을 닫으면 사라지므로 다음 방문에 다시 한 번의 기회가 생긴다.
 * 접근이 막힌 환경(프라이빗 모드 등)에서는 리로드하지 않는다. 구제받지 못하는 것보다
 * 무한 루프가 훨씬 나쁘다.
 */
export function reloadOnceForChunkError(error: unknown): boolean {
  if (typeof window === 'undefined') return false;
  if (!isChunkLoadError(error)) return false;

  const key = reloadFlagKey(window.location.pathname);

  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
  } catch {
    return false;
  }

  // 같은 URL 을 서버에서 다시 받아온다. 그 시점 서버는 새 컨테이너뿐이므로
  // 새 빌드의 HTML 과 새 청크 이름을 받는다. 페이지가 바뀌는 게 아니라 빌드가 갈아끼워진다.
  window.location.reload();
  return true;
}

/**
 * 정상적으로 화면을 그린 뒤 호출해 자동 새로고침 기회를 되돌려준다.
 *
 * 안 되돌리면 한 탭에서 그 경로는 평생 한 번만 구제된다. 며칠 열어두는 탭도 있으니
 * 배포가 두 번 일어나면 두 번째는 못 구한다.
 */
export function clearChunkReloadFlag(): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(reloadFlagKey(window.location.pathname));
  } catch {
    // 접근이 막힌 환경 — 애초에 플래그를 세우지도 못했으므로 할 일이 없다.
  }
}
